import {
    AutocompleteChangeReason,
    AutocompleteCloseReason,
    AutocompleteRenderOptionState,
    Grid,
    Theme,
} from '@mui/material';
import { CheckmarkIcon } from '@synergy/theme';
import { ChipAutocomplete, PoolButton, PoolModal, PoolTypography, ValueOrNDash } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { FormikProps } from 'formik';
import { SyntheticEvent, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { padZeroLeft } from '@synergy/utils';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import * as CustomerDetailOverviewActions from '../store/customer-detail-overview-actions';
import { BlacklistedSupplier } from '../models/blacklisted-supplier';
import { CustomerDetailOverviewFormValues } from '../models/customer-detail-overview-form-values';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    highlight: {
        color: theme.palette.primary.main,
    },
    preLine: {
        whiteSpace: 'pre-line',
    },
}));

interface CustomerDetailOverviewBlacklistProps {
    formikProps: FormikProps<CustomerDetailOverviewFormValues>;
}

export const CustomerDetailOverviewBlacklist = (props: CustomerDetailOverviewBlacklistProps) => {
    const [showDeleteAllSuppliersModal, setShowDeleteAllSuppliersModal] = useState(false);
    const { items: blacklistedSuppliers } = useAppSelector(
        state => state.contactsAndOrganizations.customers.customerDetail.overview.blacklistedSuppliers
    );
    const customer = useAppSelector(state => state.contactsAndOrganizations.customers.customerDetail.overview.customer);
    const dispatch = useThunkDispatch();
    const classes = useStyles();

    useEffect(() => {
        dispatch(CustomerDetailOverviewActions.fetchBlacklistedSuppliers(new URLSearchParams()));
    }, [customer?.id, dispatch]);

    const getOptionLabel = (supplier: BlacklistedSupplier) => {
        return `${padZeroLeft(supplier.id, 5)} ${supplier.name} ${supplier.mainAddress?.city ?? '&ndash;'}`;
    };

    const handleSupplierChange =
        (formikProps: FormikProps<CustomerDetailOverviewFormValues>) =>
        (_event: SyntheticEvent<Element, Event>, supplier: BlacklistedSupplier[], reason: AutocompleteChangeReason) => {
            if (reason === 'clear') {
                setShowDeleteAllSuppliersModal(true);
            } else {
                formikProps.setFieldValue('blacklistedSuppliers', supplier);
            }
        };

    const handleTextFieldChange = (_event: SyntheticEvent<Element, Event>, value: string) => {
        if (value) {
            dispatch(CustomerDetailOverviewActions.fetchBlacklistedSuppliers(new URLSearchParams({ search: value })));
        }
    };

    const renderOption = (supplier: BlacklistedSupplier, state: AutocompleteRenderOptionState) => {
        const nameMatches = match(supplier.name, state.inputValue);
        const nameParts = parse(supplier.name, nameMatches);

        return (
            <>
                <div>
                    <PoolTypography>{padZeroLeft(supplier.id, 5)}</PoolTypography>
                </div>
                <div style={{ flex: 1 }}>
                    {nameParts.map(namePart => (
                        <span className={namePart.highlight ? classes.highlight : ''}>{namePart.text}</span>
                    ))}
                </div>
                <div style={{ width: '30%' }}>
                    <ValueOrNDash value={supplier.mainAddress?.city} />
                </div>
            </>
        );
    };

    const handleResetSuppliers = (_event: SyntheticEvent<Element, Event>, reason: AutocompleteCloseReason) => {
        if (reason === 'blur') {
            dispatch(CustomerDetailOverviewActions.fetchBlacklistedSuppliers(new URLSearchParams()));
        }
    };

    const actionChildren = (formikProps: FormikProps<CustomerDetailOverviewFormValues>) => {
        return [
            <PoolButton
                key="2"
                styleType="firstLevel"
                variant="text"
                onClick={() => setShowDeleteAllSuppliersModal(false)}
            >
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>,
            <PoolButton key="1" styleType="firstLevel" onClick={() => handleDeleteAllSuppliers(formikProps)}>
                <FormattedMessage id="app.common.unlock" />
            </PoolButton>,
        ];
    };

    const options = (formikProps: FormikProps<CustomerDetailOverviewFormValues>) => {
        return blacklistedSuppliers.filter(
            supplier =>
                !formikProps.values.blacklistedSuppliers
                    ?.map(blacklistedSupplier => blacklistedSupplier.id)
                    .includes(supplier.id)
        );
    };

    const handleDeleteAllSuppliers = (formikProps: FormikProps<CustomerDetailOverviewFormValues>) => {
        formikProps.setFieldValue('blacklistedSuppliers', []);
        setShowDeleteAllSuppliersModal(false);
    };

    return (
        <Grid container={true} spacing={2}>
            <Grid item={true} xs={6}>
                <PoolTypography
                    typography="body1"
                    id="app.contactsAndOrganizations.detail.sftp.suppliersDetails"
                    className={classes.preLine}
                />
            </Grid>
            <Grid item={true} xs={8}>
                <ChipAutocomplete
                    noOptionsText={<FormattedMessage id="app.common.emptyResult" />}
                    options={options(props.formikProps)}
                    renderOption={renderOption}
                    getOptionLabel={getOptionLabel}
                    getChipLabel={(supplier: BlacklistedSupplier) => supplier.name}
                    onChange={handleSupplierChange(props.formikProps)}
                    value={props.formikProps.values.blacklistedSuppliers || []}
                    textfieldPlaceholder="app.customer.supplierSearch"
                    onTextFieldValueChange={handleTextFieldChange}
                    chipColor="secondary"
                    onClose={handleResetSuppliers}
                />
            </Grid>
            {props.formikProps.values.blacklistedSuppliers?.length === 0 && (
                <Grid item={true} xs={6} style={{ display: 'flex' }}>
                    <CheckmarkIcon style={{ fontSize: 20, marginRight: 8 }} />
                    <PoolTypography typography="body2" id="app.customer.allSuppliers" />
                </Grid>
            )}

            <PoolModal
                title={<FormattedMessage id="app.customer.unlockSuppliers" />}
                isVisible={showDeleteAllSuppliersModal}
                handleClose={() => setShowDeleteAllSuppliersModal(false)}
                actionChildren={actionChildren(props.formikProps)}
            >
                <PoolTypography id="app.customer.unlockSuppliersWarning" />
            </PoolModal>
        </Grid>
    );
};
